import { useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';
import { validateWhat3Words } from '../api/sitesApi';
import { SiteImportFormData } from '../schemas/siteImportSchema';
import { VALIDATION_DEBOUNCE_MS } from './validationConstants';

export function useWhat3WordsValidation(
  form: UseFormReturn<SiteImportFormData>
) {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCheckedAddressRef = useRef<string>('');

  const mutation = useMutation({
    mutationFn: (words: string) => validateWhat3Words(words),
    onSuccess: (result, words) => {
      if (words.toLowerCase() === lastCheckedAddressRef.current.toLowerCase()) {
        if (result.isValid) {
          form.clearErrors('what3words');
          form.setValue('latitude', result.latitude ?? null);
          form.setValue('longitude', result.longitude ?? null);
        } else {
          form.setError('what3words', {
            type: 'manual',
            message: result.errorMessage ?? 'Address could not be validated',
          });
        }
      }
    },
  });

  const mutateRef = useRef(mutation.mutate);
  mutateRef.current = mutation.mutate;

  const validateAddress = useCallback((words: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmed = words.trim();
    lastCheckedAddressRef.current = trimmed;

    if (!/^[a-zA-Z]+\.[a-zA-Z]+\.[a-zA-Z]+$/.test(trimmed)) {
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      mutateRef.current(trimmed);
    }, VALIDATION_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return { validateAddress, isChecking: mutation.isPending };
}
